import type { ShipmentListItemTrackingApi } from '@/views/screens/Shipment/types';
import type {
  ShipmentTrackingNotesAndDetails,
  ShipmentTrackingProductInfo,
  ShipmentTrackingSearchFilters,
  ShipmentTrackingShipperConsigneeData,
  ShipmentTrackingStatus,
} from '@/views/screens/VisibilityScreen/types';
import { convertDateUTCToUserLocal } from '../../utilz/dates';
import { DEFAULT_STORE_OBJECT } from '../constants';
import type { DefaultStoreObject } from '../types';
import VisibilityTypes from './VisibilityTypes';

export interface VisibilityState {
  visibilitySearchList: DefaultStoreObject<ShipmentListItemTrackingApi[]> & {
    req: ShipmentTrackingSearchFilters;
    footerData: { totalShipment: number; openShipment: number; closeShipment: number; exceptionShipment: number }[];
    count?: {
      totalCount?: number;
    };
  };
  trackingStatusDetails: DefaultStoreObject<ShipmentTrackingStatus[]>;
  productInfo: DefaultStoreObject<ShipmentTrackingProductInfo[]>;
  getShipmentNotesetails: DefaultStoreObject<ShipmentTrackingNotesAndDetails>;
  getShipperConsigneeDetails: DefaultStoreObject<{
    shipperData: ShipmentTrackingShipperConsigneeData;
    consigneeData: ShipmentTrackingShipperConsigneeData;
  }>;
  editGeneralBtnInfo: {
    isBtnClicked: boolean;
  };
  editDetailsBtnInfo: {
    isBtnClicked: boolean;
  };
  addNewStatusAction: {
    btnCliked: boolean;
  };
  addnewfileactions: {
    btnCliked: boolean;
  };
  selectedVisibility: DefaultStoreObject<ShipmentListItemTrackingApi>;
  updateRedAndDockNoInfo: DefaultStoreObject<string>;
  updateShipmentTrackingDetailsData: DefaultStoreObject<string | { status: number }>;
  excelData: DefaultStoreObject<ShipmentListItemTrackingApi[]>;
  exceptionData: unknown;
}

const initialState: VisibilityState = {
  visibilitySearchList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    footerData: [],
    req: {
      shipmentType: 'O',
      customerKey: '',
      carrierKey: '',
      vendorKey: '',
      pageIndex: 1,
      pageSize: 20,
      statusCode: '',
      searchFlag: '',
    },
    progressing: false,
  },
  trackingStatusDetails: {
    ...DEFAULT_STORE_OBJECT,
    progressing: false,
    data: [],
  },
  productInfo: {
    ...DEFAULT_STORE_OBJECT,
    progressing: false,
    data: [],
  },
  getShipmentNotesetails: {
    ...DEFAULT_STORE_OBJECT,
    progressing: false,
  },
  getShipperConsigneeDetails: {
    ...DEFAULT_STORE_OBJECT,
    progressing: false,
  },
  updateShipmentTrackingDetailsData: {
    ...DEFAULT_STORE_OBJECT,
    progressing: false,
  },
  exceptionData: {
    // TODO: investigate API
    ...DEFAULT_STORE_OBJECT,
    progressing: false,
    data: [],
  },
  addnewfileactions: {
    btnCliked: false,
  },
  addNewStatusAction: {
    btnCliked: false,
  },
  selectedVisibility: {
    ...DEFAULT_STORE_OBJECT,
  },
  excelData: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  editGeneralBtnInfo: {
    isBtnClicked: false,
  },
  editDetailsBtnInfo: {
    isBtnClicked: false,
  },
  updateRedAndDockNoInfo: {
    ...DEFAULT_STORE_OBJECT,
  },
};

// biome-ignore lint/style/useDefaultParameterLast: can't be modified
const VisibilityReducer = (state = initialState, { type, payload }: any): VisibilityState => {
  switch (type) {
    case VisibilityTypes.UPDATE_VISIBILITY_SEARCH_LIST_SUCCESS: {
      return {
        ...state,
        visibilitySearchList: {
          ...state.visibilitySearchList,
          data: payload.data,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_SEARCH_LIST: {
      return {
        ...state,
        visibilitySearchList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.visibilitySearchList,
          req: {
            ...state.visibilitySearchList.req,
            ...payload,
          },
          progressing: true,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_SEARCH_LIST_SUCCESS: {
      return {
        ...state,
        visibilitySearchList: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload.data,
          req: state.visibilitySearchList.req,
          data: [...(state.visibilitySearchList.data || []), ...payload.data.totalData],
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_SEARCH_LIST_FAIL: {
      return {
        ...state,
        visibilitySearchList: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
          req: state.visibilitySearchList.req,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_SEARCH_LIST_CLEAR: {
      return {
        ...state,
        visibilitySearchList: {
          ...state.visibilitySearchList,
          data: [],
          footerData: [],
          req: {
            ...state.visibilitySearchList.req,
            shipmentType: 'O',
            customerKey: '',
            carrierKey: '',
            vendorKey: '',
            pageIndex: 1,
            pageSize: 20,
            statusCode: '',
            searchFlag: '',
          },
        },
      };
    }
    case VisibilityTypes.GET_VISIBILITY_SET_SEARCH_LIST_DATA: {
      return {
        ...state,
        visibilitySearchList: {
          ...state.visibilitySearchList,
          data: [],
          footerData: [],
          req: {
            ...state.visibilitySearchList.req,
            pageIndex: 1,
            pageSize: 20,
          },
        },
      };
    }

    case VisibilityTypes.GET_TRACKING_STATUS_DETAILS: {
      return {
        ...state,
        trackingStatusDetails: {
          ...DEFAULT_STORE_OBJECT,
          ...state.trackingStatusDetails,
          data: [],
          progressing: true,
        },
      };
    }

    case VisibilityTypes.GET_TRACKING_STATUS_DETAILS_SUCCESS: {
      return {
        ...state,
        trackingStatusDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload.data,
          // @ts-ignore
          data: payload.data.map((element) => ({
            ...element,
            dateTimeStamp: convertDateUTCToUserLocal(element.dateTimeStamp),
            eventOccurrence: convertDateUTCToUserLocal(element.eventOccurrence),
          })),
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_TRACKING_STATUS_DETAILS_FAIL: {
      return {
        ...state,
        trackingStatusDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_PRODUCT_INFO: {
      return {
        ...state,
        productInfo: {
          ...DEFAULT_STORE_OBJECT,
          ...state.productInfo,
          data: [],
          progressing: true,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_PRODUCT_INFO_SUCCESS: {
      return {
        ...state,
        productInfo: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload.data,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_PRODUCT_INFO_FAIL: {
      return {
        ...state,
        productInfo: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_SHIPMENT_NOTES_AND_DETAILS: {
      return {
        ...state,
        getShipmentNotesetails: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case VisibilityTypes.GET_SHIPMENT_NOTES_AND_DETAILS_SUCCESS: {
      return {
        ...state,
        getShipmentNotesetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_SHIPMENT_NOTES_AND_DETAILS_FAIL: {
      return {
        ...state,
        getShipmentNotesetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_SHIPPER_AND_CONSIGNEE_DETAILS: {
      return {
        ...state,
        getShipperConsigneeDetails: {
          ...DEFAULT_STORE_OBJECT,
          ...state.getShipperConsigneeDetails,
          progressing: true,
        },
      };
    }

    case VisibilityTypes.GET_SHIPPER_AND_CONSIGNEE_DETAILS_SUCCESS: {
      return {
        ...state,
        getShipperConsigneeDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_SHIPPER_AND_CONSIGNEE_DETAILS_FAIL: {
      return {
        ...state,
        getShipperConsigneeDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
          progressing: false,
        },
      };
    }

    case VisibilityTypes.UPDATE_SHIPMENT_TRACKING_DETAILS: {
      return {
        ...state,
        updateShipmentTrackingDetailsData: {
          ...DEFAULT_STORE_OBJECT,
          ...state.updateShipmentTrackingDetailsData,
          progressing: true,
        },
      };
    }

    case VisibilityTypes.UPDATE_SHIPMENT_TRACKING_DETAILS_SUCCESS: {
      return {
        ...state,
        updateShipmentTrackingDetailsData: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.UPDATE_SHIPMENT_TRACKING_DETAILS_FAIL: {
      return {
        ...state,
        updateShipmentTrackingDetailsData: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
          progressing: false,
        },
      };
    }

    case VisibilityTypes.UPDATE_SHIPMENT_TRACKING_DETAILS_CLEAR: {
      return {
        ...state,
        updateShipmentTrackingDetailsData: {
          ...DEFAULT_STORE_OBJECT,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_EXCEPTION: {
      return {
        ...state,
        exceptionData: {
          ...DEFAULT_STORE_OBJECT,
          ...(state.exceptionData || []),
          data: [],
          progressing: true,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_EXCEPTION_SUCCESS: {
      return {
        ...state,
        exceptionData: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_EXCEPTION_FAIL: {
      return {
        ...state,
        exceptionData: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_EXCEPTION_CLEAR: {
      return {
        ...state,
        exceptionData: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: false,
        },
      };
    }

    case VisibilityTypes.ADD_NEW_FILE_ACTION: {
      return {
        ...state,
        addnewfileactions: {
          btnCliked: payload,
        },
      };
    }

    case VisibilityTypes.ADD_NEW_TRACKING_STATUS_ACTION: {
      return {
        ...state,
        addNewStatusAction: {
          btnCliked: payload,
        },
      };
    }

    case VisibilityTypes.SET_SELECTED_VISIBILITY: {
      return {
        ...state,
        selectedVisibility: {
          ...DEFAULT_STORE_OBJECT,
          data: payload,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.CLEAR_SELECTED_VISIBILITY: {
      return {
        ...state,
        selectedVisibility: {
          ...DEFAULT_STORE_OBJECT,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_EXCEL_DATA: {
      return {
        ...state,
        excelData: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_EXCEL_DATA_SUCCESS: {
      return {
        ...state,
        excelData: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.GET_VISIBILITY_EXCEL_DATA_FAIL: {
      return {
        ...state,
        excelData: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: payload,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.EDIT_GENETRAL_BTN_ACTION: {
      return {
        ...state,
        editGeneralBtnInfo: {
          isBtnClicked: payload === undefined ? !state.editGeneralBtnInfo.isBtnClicked : payload,
        },
      };
    }

    case VisibilityTypes.EDIT_DETAILS_BTN_ACTION: {
      return {
        ...state,
        editDetailsBtnInfo: {
          isBtnClicked: payload === undefined ? !state.editDetailsBtnInfo.isBtnClicked : payload,
        },
      };
    }

    case VisibilityTypes.UPDATE_REF_AND_DOCK_NO: {
      return {
        ...state,
        updateRedAndDockNoInfo: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case VisibilityTypes.UPDATE_REF_AND_DOCK_NO_SUCCESS: {
      return {
        ...state,
        updateRedAndDockNoInfo: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };
    }

    case VisibilityTypes.UPDATE_REF_AND_DOCK_NO_FAIL: {
      return {
        ...state,
        updateRedAndDockNoInfo: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: payload,
          progressing: false,
        },
      };
    }

    default:
      return state;
  }
};

export default VisibilityReducer;
